import {
  BRAND_LOGO_LABELS,
  emptyQrContent,
  parseQrContent,
  QR_CONTENT_KINDS,
  QR_CONTENT_LABELS,
  QR_ECC_LEVELS,
  QR_LOGO_ECC,
  QR_LOGO_MAX_SIZE,
  QR_LOGO_MIN_SIZE,
  QR_MAX_LENGTH,
  QR_PROBLEM_LABELS,
  type QrContent,
  type QrContentKind,
  type QrEcc,
  type QrElement,
  type QrStyle,
  qrMatrix,
  qrProblems,
  qrValue,
  withAlpha,
} from "@cva/shared";
import { AlertTriangle, ExternalLink, ImagePlus, QrCode, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import { MediaPickerDialog } from "../components/media-picker";
import { Input, Select, Textarea } from "../components/ui/input";
import { cn } from "../lib/utils";
import { type BrandLink, ColorButton } from "./ColorPicker";
import { addQr } from "./commands";
import { useEnv } from "./env";
import { useEditor, useEditorApi } from "./store";
import { NumberField, Row, Section, Segmented, Slider, Toggle } from "./ui";

/**
 * QR code element: insert from the elements panel, edit content (link,
 * text, e-mail, phone, Wi-Fi, contact), colours, look and error correction
 * in the properties panel.
 */

/** Error correction: share of the code that may be damaged or covered. */
const ECC_LABELS: Record<QrEcc, string> = {
  L: "Robust 7 %",
  M: "Robust 15 %",
  Q: "Robust 25 %",
  H: "Robust 30 %",
};

const STYLE_OPTIONS: { value: QrStyle; label: string; title: string }[] = [
  { value: "square", label: "Eckig", title: "Klassische quadratische Punkte" },
  { value: "rounded", label: "Weich", title: "Abgerundete Punkte und Ecken" },
  { value: "dots", label: "Punkte", title: "Runde Punkte" },
];

/** The content of a value as the chosen kind (plain texts and links are the value itself). */
function contentFor(kind: QrContentKind, value: string): QrContent {
  if (kind === "url") return { kind, url: value };
  if (kind === "text") return { kind, text: value };
  const parsed = parseQrContent(value);
  return parsed.kind === kind ? parsed : emptyQrContent(kind);
}

/** Link or text field in the elements panel: inserts a QR code in one step. */
export function QrInsert() {
  const store = useEditorApi();
  const [value, setValue] = useState("");
  const insert = () => {
    const text = value.trim();
    addQr(store, text || "https://");
    setValue("");
  };
  return (
    <div className="space-y-2">
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          insert();
        }}
      >
        <Input
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="https://… oder Text"
          maxLength={QR_MAX_LENGTH}
          className="h-9 min-w-0 flex-1 text-sm"
          aria-label="Inhalt des QR-Codes"
        />
        <button
          type="submit"
          title="QR-Code einfügen"
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 text-zinc-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 [&_svg]:size-5"
        >
          <QrCode />
        </button>
      </form>
      <p className="text-[11px] text-zinc-500">
        Link, Text, WLAN-Zugang oder Kontakt – alles Weitere rechts im Panel.
      </p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-zinc-500">{label}</span>
      {children}
    </label>
  );
}

function ContentFields({
  content,
  onChange,
}: {
  content: QrContent;
  onChange: (content: QrContent) => void;
}) {
  const input = "h-8 text-sm";
  switch (content.kind) {
    case "url":
      return (
        <Field label="Link">
          <Input
            className={input}
            value={content.url}
            onChange={(event) => onChange({ ...content, url: event.target.value })}
            placeholder="https://beispiel.de/aktion"
          />
        </Field>
      );
    case "text":
      return (
        <Field label="Text">
          <Textarea
            className="min-h-16 text-sm"
            value={content.text}
            onChange={(event) => onChange({ ...content, text: event.target.value })}
          />
        </Field>
      );
    case "email":
      return (
        <>
          <Field label="E-Mail-Adresse">
            <Input
              className={input}
              type="email"
              value={content.email}
              onChange={(event) => onChange({ ...content, email: event.target.value })}
              placeholder="info@beispiel.de"
            />
          </Field>
          <Field label="Betreff (optional)">
            <Input
              className={input}
              value={content.subject}
              onChange={(event) => onChange({ ...content, subject: event.target.value })}
            />
          </Field>
        </>
      );
    case "phone":
      return (
        <Field label="Telefonnummer">
          <Input
            className={input}
            type="tel"
            value={content.phone}
            onChange={(event) => onChange({ ...content, phone: event.target.value })}
            placeholder="+49 30 1234567"
          />
        </Field>
      );
    case "wifi":
      return (
        <>
          <Field label="Netzwerkname (SSID)">
            <Input
              className={input}
              value={content.ssid}
              onChange={(event) => onChange({ ...content, ssid: event.target.value })}
            />
          </Field>
          <Field label="Verschlüsselung">
            <Select
              className={input}
              value={content.security}
              onChange={(event) =>
                onChange({
                  ...content,
                  security: event.target.value as "WPA" | "WEP" | "nopass",
                })
              }
            >
              <option value="WPA">WPA/WPA2/WPA3</option>
              <option value="WEP">WEP</option>
              <option value="nopass">Offen (ohne Passwort)</option>
            </Select>
          </Field>
          {content.security !== "nopass" ? (
            <Field label="Passwort">
              <Input
                className={input}
                value={content.password}
                onChange={(event) => onChange({ ...content, password: event.target.value })}
              />
            </Field>
          ) : null}
          <Toggle
            checked={content.hidden}
            onChange={(hidden) => onChange({ ...content, hidden })}
            label="Verstecktes Netzwerk"
          />
        </>
      );
    case "vcard":
      return (
        <>
          {(
            [
              ["name", "Name", "Maria Muster"],
              ["organization", "Firma", ""],
              ["phone", "Telefon", "+49 …"],
              ["email", "E-Mail", ""],
              ["url", "Website", "https://"],
              ["address", "Adresse", "Straße 1, 12345 Stadt"],
            ] as const
          ).map(([key, label, placeholder]) => (
            <Field key={key} label={label}>
              <Input
                className={input}
                value={content[key]}
                placeholder={placeholder}
                onChange={(event) => onChange({ ...content, [key]: event.target.value })}
              />
            </Field>
          ))}
        </>
      );
  }
}

const CHECKERBOARD =
  "bg-[repeating-conic-gradient(#f4f4f5_0%_25%,#fff_0%_50%)] bg-[length:12px_12px]";

/**
 * Logo in the middle of the code: a brand kit logo or any image/graphic of
 * the media library. Choosing one raises the error correction, so the code
 * still reads with the covered modules.
 */
function LogoFields({
  element,
  set,
}: {
  element: QrElement;
  set: (recipe: (target: QrElement) => void, merge?: string) => void;
}) {
  const env = useEnv();
  const [picking, setPicking] = useState(false);
  const logo = element.logo;
  const asset = logo ? env.assets.get(logo.assetId) : undefined;
  const brandLogos = env.brandKit?.logos ?? [];
  const choose = (assetId: string) => {
    const raise = QR_ECC_LEVELS.indexOf(element.ecc) < QR_ECC_LEVELS.indexOf(QR_LOGO_ECC);
    set((target) => {
      target.logo = { assetId, size: target.logo?.size ?? 0.22, clear: target.logo?.clear ?? true };
      if (raise) target.ecc = QR_LOGO_ECC;
    });
    if (raise) {
      toast.info(
        `Fehlerkorrektur auf „${ECC_LABELS[QR_LOGO_ECC]}“ erhöht, damit der Code mit Logo lesbar bleibt.`,
      );
    }
  };
  const tile =
    "flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-zinc-200 p-1";
  return (
    <div className="space-y-2">
      <p className="text-xs text-zinc-500">Logo in der Mitte</p>
      {logo ? (
        <>
          <div className="flex items-center gap-2">
            <div className={cn(tile, CHECKERBOARD)}>
              {asset ? (
                <img
                  src={asset.thumbnailUrl ?? asset.url}
                  alt=""
                  className="max-h-full max-w-full object-contain"
                  draggable={false}
                />
              ) : null}
            </div>
            <span className="min-w-0 flex-1 truncate text-sm text-zinc-700">
              {asset?.name ?? "Logo"}
            </span>
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="h-8 rounded-md border border-zinc-200 px-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            >
              Ändern
            </button>
            <button
              type="button"
              title="Logo entfernen"
              aria-label="Logo entfernen"
              onClick={() => set((target) => delete target.logo)}
              className="inline-flex size-8 items-center justify-center rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800 [&_svg]:size-4"
            >
              <X />
            </button>
          </div>
          <label className="block space-y-1">
            <span className="flex justify-between text-xs text-zinc-500">
              <span>Größe</span>
              <span>{Math.round(logo.size * 100)} %</span>
            </span>
            <Slider
              value={Math.round(logo.size * 100)}
              min={QR_LOGO_MIN_SIZE * 100}
              max={QR_LOGO_MAX_SIZE * 100}
              onChange={(percent) =>
                set((target) => {
                  if (target.logo) target.logo.size = percent / 100;
                }, "logo-size")
              }
            />
          </label>
          <Toggle
            checked={logo.clear}
            onChange={(clear) =>
              set((target) => {
                if (target.logo) target.logo.clear = clear;
              })
            }
            label="Punkte unter dem Logo aussparen"
            description="Freie Fläche in der Hintergrundfarbe – so lesen Kameras den Code am sichersten."
          />
        </>
      ) : (
        <div className="flex flex-wrap gap-2">
          {brandLogos.map((entry) => (
            <button
              key={entry.variant}
              type="button"
              title={`${BRAND_LOGO_LABELS[entry.variant]} einsetzen`}
              onClick={() => choose(entry.asset.id)}
              className={cn(tile, CHECKERBOARD, "hover:ring-2 hover:ring-brand-400")}
            >
              <img
                src={entry.asset.thumbnailUrl ?? entry.asset.url}
                alt={BRAND_LOGO_LABELS[entry.variant]}
                className="max-h-full max-w-full object-contain"
                draggable={false}
              />
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPicking(true)}
            className={cn(
              "inline-flex h-12 items-center justify-center gap-1.5 rounded-md border border-dashed border-zinc-300 px-3 text-sm font-medium text-zinc-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700",
              brandLogos.length === 0 && "w-full",
            )}
          >
            <ImagePlus className="size-4" /> Logo wählen
          </button>
        </div>
      )}
      {picking ? (
        <MediaPickerDialog
          orgId={env.orgId}
          open
          onOpenChange={setPicking}
          title="Logo für den QR-Code"
          description="Am besten ein quadratisches Logo oder Icon mit transparentem Hintergrund."
          onPick={([picked]) => {
            setPicking(false);
            if (picked && (picked.kind === "image" || picked.kind === "svg")) choose(picked.id);
          }}
        />
      ) : null}
    </div>
  );
}

export function QrProperties({
  element,
  colorLink,
  backgroundLink,
}: {
  element: QrElement;
  colorLink?: BrandLink | undefined;
  backgroundLink?: BrandLink | undefined;
}) {
  const store = useEditorApi();
  const page = useEditor((s) => s.doc.pages[s.pageIndex]);
  const [content, setContent] = useState<QrContent>(() =>
    contentFor(parseQrContent(element.value).kind, element.value),
  );
  // Undo/redo or another panel changed the value: follow it.
  useEffect(() => {
    setContent((current) =>
      qrValue(current) === element.value
        ? current
        : contentFor(parseQrContent(element.value).kind, element.value),
    );
  }, [element.value]);
  const set = (recipe: (target: QrElement) => void, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.elements[element.id];
        if (target?.type === "qr") recipe(target);
      },
      merge ? { merge: `qr-${element.id}-${merge}` } : undefined,
    );
  const changeContent = (next: QrContent) => {
    setContent(next);
    const value = qrValue(next);
    // An empty code is not a valid document; keep the last value until there is text.
    if (value.trim() && value.length <= QR_MAX_LENGTH)
      set((target) => (target.value = value), "value");
  };
  const behind = typeof page?.background === "string" ? page.background : "#ffffff";
  const problems = qrProblems(element, behind);
  const matrix = qrMatrix(element.value, element.ecc);
  const transparent = element.background.length === 9 && element.background.endsWith("00");
  const link =
    content.kind === "url" && /^https?:\/\/\S+\.\S+/i.test(content.url) ? content.url : null;
  return (
    <Section title="QR-Code">
      <Select
        className="h-8"
        aria-label="Art des Inhalts"
        value={content.kind}
        onChange={(event) => {
          const kind = event.target.value as QrContentKind;
          changeContent(
            kind === "text" ? { kind, text: qrValue(content) } : contentFor(kind, element.value),
          );
        }}
      >
        {QR_CONTENT_KINDS.map((kind) => (
          <option key={kind} value={kind}>
            {QR_CONTENT_LABELS[kind]}
          </option>
        ))}
      </Select>
      <ContentFields content={content} onChange={changeContent} />
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
        >
          <ExternalLink className="size-3.5" /> Link testen
        </a>
      ) : null}
      {problems.length > 0 ? (
        <div className="space-y-1 rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
          {problems.map((problem) => (
            <p key={problem} className="flex gap-1.5">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              {QR_PROBLEM_LABELS[problem]}
            </p>
          ))}
        </div>
      ) : null}
      <Row>
        <span className="w-20 shrink-0 text-xs text-zinc-500">Code</span>
        <ColorButton
          value={element.color}
          onChange={(color) => set((target) => (target.color = color))}
          brandLink={colorLink}
        />
      </Row>
      <Row>
        <span className="w-20 shrink-0 text-xs text-zinc-500">Hintergrund</span>
        {transparent ? (
          <span className="text-xs text-zinc-500">Transparent</span>
        ) : (
          <ColorButton
            value={element.background}
            onChange={(color) => set((target) => (target.background = color))}
            brandLink={backgroundLink}
          />
        )}
      </Row>
      <Toggle
        checked={transparent}
        onChange={(checked) =>
          set((target) => {
            target.background = checked ? withAlpha("#ffffff", 0) : "#ffffff";
            if (target.brand?.fill) {
              const { fill: _fill, ...rest } = target.brand;
              target.brand = Object.keys(rest).length > 0 ? rest : undefined;
            }
          })
        }
        label="Transparenter Hintergrund"
        description="Nur auf hellem, ruhigem Untergrund gut lesbar."
      />
      <Segmented
        className="w-full"
        value={element.style}
        onChange={(style) => set((target) => (target.style = style))}
        options={STYLE_OPTIONS}
      />
      <LogoFields element={element} set={set} />
      <div className="grid grid-cols-2 gap-2">
        <NumberField
          label="Rand"
          value={element.margin}
          min={0}
          max={10}
          step={1}
          onChange={(v) =>
            set((target) => (target.margin = Math.max(0, Math.min(10, Math.round(v)))), "margin")
          }
        />
        <Select
          className="h-8 text-xs"
          aria-label="Fehlerkorrektur"
          title="Fehlerkorrektur: je höher, desto robuster (z. B. bei Knicken oder einem Logo darüber), aber dichter"
          value={element.ecc}
          onChange={(event) => set((target) => (target.ecc = event.target.value as QrEcc))}
        >
          {(Object.keys(ECC_LABELS) as QrEcc[]).map((ecc) => (
            <option key={ecc} value={ecc}>
              {ECC_LABELS[ecc]}
            </option>
          ))}
        </Select>
      </div>
      <p className={cn("text-[11px] text-zinc-500")}>
        {matrix
          ? `${matrix.size} × ${matrix.size} Punkte. Kürzere Inhalte ergeben größere, leichter lesbare Punkte.`
          : "Der Inhalt passt nicht in einen QR-Code."}
      </p>
    </Section>
  );
}
